import { SelectedTab } from 'features/navbar/types'
import { MODEL_KIND_LABEL } from 'features/threeDModels/modelKinds'
import { type ModelKind, modelKindSchema, type RecentRoute } from 'types'

import type { CommandPalettePageTarget } from './commandPaletteTargets'

/**
 * What a recorded route was about: a sidebar page, one asset row shown on the
 * Assets page, or one of that asset's 3D models. Two routes with the same
 * identity are the same Recent row, whatever their URLs.
 */
export interface RecentRouteIdentity {
  destination: SelectedTab
  assetKey?: string
  unitId?: string
  view?: ModelKind
}

export interface IdentifiedRecentRoute {
  route: RecentRoute
  identity: RecentRouteIdentity
}

const UNIT_ROW_KEY_PREFIX = 'unit:'

const stringParam = (
  search: RecentRoute['search'],
  key: string
): string | undefined => {
  const value = search?.[key]
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Asset pages carry `unitId`; the 3D viewer carries the row key as `asset`,
 * `unit:<id>` or `space:<id>`. Only routes on the Assets page count: a
 * document linked to a unit has a `unitId` too but is not about that asset.
 */
const identifyRecentRoute = (
  destination: SelectedTab,
  search: RecentRoute['search']
): RecentRouteIdentity => {
  if (destination !== SelectedTab.Assets) return { destination }
  const unitIdParam = stringParam(search, 'unitId')
  const assetKey = unitIdParam
    ? `${UNIT_ROW_KEY_PREFIX}${unitIdParam}`
    : stringParam(search, 'asset')
  if (!assetKey) return { destination }
  const unitId = assetKey.startsWith(UNIT_ROW_KEY_PREFIX)
    ? assetKey.slice(UNIT_ROW_KEY_PREFIX.length)
    : undefined
  const view = modelKindSchema.safeParse(search?.view)
  return {
    destination,
    assetKey,
    ...(unitId ? { unitId } : {}),
    ...(view.success ? { view: view.data } : {}),
  }
}

export const recentRouteKey = (identity: RecentRouteIdentity): string =>
  [identity.destination, identity.assetKey ?? '', identity.view ?? ''].join(':')

/**
 * Routes newest first, one per identity, routes off every sidebar page
 * dropped. Dedupe is by identity alone: labels come later and may collide.
 */
export function identifyRecentRoutes(
  routes: readonly RecentRoute[],
  destinationOf: (pathname: string) => SelectedTab | undefined
): IdentifiedRecentRoute[] {
  const seen = new Set<string>()
  return routes.flatMap((route) => {
    const destination = destinationOf(route.pathname)
    if (!destination) return []
    const identity = identifyRecentRoute(destination, route.search)
    const key = recentRouteKey(identity)
    if (seen.has(key)) return []
    seen.add(key)
    return [{ route, identity }]
  })
}

/**
 * Undefined for an asset row without a name to show, whether the unit's name
 * has not resolved yet or the row is not a unit: it is withheld rather than
 * shown under the page's own name.
 */
export function recentRouteLabel(
  identity: RecentRouteIdentity,
  unitName: string | undefined,
  pageLabel: string
): string | undefined {
  if (!identity.assetKey) return pageLabel
  if (!unitName) return undefined
  return identity.view
    ? `${unitName} · ${MODEL_KIND_LABEL[identity.view]}`
    : unitName
}

/**
 * The sidebar pages not already listed under Recent. A recent about an asset
 * is not the Assets page, however it is named, so it hides nothing.
 */
export function pagesNotListedAsRecent(
  pages: readonly CommandPalettePageTarget[],
  recents: readonly CommandPalettePageTarget[]
): CommandPalettePageTarget[] {
  const listed = new Set(
    recents.flatMap((recent) => (recent.unitId ? [] : [recent.destination]))
  )
  return pages.filter((page) => !listed.has(page.destination))
}
